import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AttachmentsResource,
  axios,
  getEcosystemNameFromFlowType,
  SyncDocumentAttributes,
} from '@diana/core';

interface AttachmentMetadata {
  unsafeOriginalFileName?: string;
  title?: string;
  fileType?: string;
  fullUri?: string;
  data?: {
    unsafeOriginalFileName?: string;
    fileType?: string;
    fullUri?: string;
  };
}

export const OpenFilePage = () => {
  const { attachmentId, resourceId, resourceType } = useParams<{
    attachmentId: string;
    resourceType: AttachmentsResource;
    resourceId: string;
  }>();

  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [fileType, setFileType] = useState<string>('');
  const [isDownload, setIsDownload] = useState<boolean>(false);
  const [flowType, setFlowType] = useState<string>('');

  const downloadFile = useCallback(
    (redirectUrl: string, fileType: string) => {
      try {
        if (
          fileType.endsWith('.docx') ||
          fileType.endsWith('.doc') ||
          fileType.endsWith('.xlsx') ||
          fileType.endsWith('.pptx') ||
          fileType.endsWith('.xls') ||
          fileType.endsWith('.txt')
        ) {
          setIsDownload(true);

          let tempLink = document.createElement('a');
          tempLink.href = redirectUrl;
          tempLink.setAttribute('download', fileName);
          tempLink.click();
        } else {
          setIsDownload(false);
        }
      } catch (error) {
        console.log(error);
      }
    },
    [fileName],
  );

  const loadFile = async () => {
    try {
      const { data: attachmentData } = await axios
        .api()
        .get<AttachmentMetadata>(`/api/v3/attachments/${attachmentId}`, {
          params: { resourceId, resourceType },
        });

      const resolvedFileName =
        attachmentData.data?.unsafeOriginalFileName ??
        attachmentData.unsafeOriginalFileName ??
        attachmentData.title ??
        'file';
      setFileName(resolvedFileName);
      setFileType(attachmentData.data?.fileType ?? attachmentData.fileType ?? '');

      const fullUri = attachmentData.data?.fullUri ?? attachmentData.fullUri;
      if (!fullUri) return;

      const fileResponse = await axios.api().get<Blob>(fullUri, { responseType: 'blob' });
      const fileBlob = fileResponse.data;
      const contentType =
        fileResponse.headers['content-type'] || fileBlob.type || 'application/octet-stream';

      const url = window.URL.createObjectURL(
        new Blob([fileBlob], {
          type: contentType === 'text/plain' ? 'application/txt' : contentType,
        }),
      );
      setRedirectUrl(url);
    } catch (e) {
      console.log('load file error', e);
    }
  };

  const loadWork = async () => {
    if (!resourceId || resourceType !== AttachmentsResource.WORK) return;
    try {
      const { data } = await axios.api().get<{ flowType?: string }>(`/api/v3/work/${resourceId}`);

      setFlowType(data.flowType ?? '');
    } catch (e) {
      console.log('load work error', e);
    }
  };

  useEffect(() => {
    if (fileType && redirectUrl) {
      downloadFile(redirectUrl, fileType);
    }
  }, [redirectUrl, fileType, downloadFile]);

  useEffect(() => {
    loadFile();
    loadWork();
    // eslint-disable-next-line
  }, []);

  return (
    <SyncDocumentAttributes environment={getEcosystemNameFromFlowType({ flowType }).environment}>
      {redirectUrl && fileName && !isDownload && (
        <iframe
          title={fileName}
          src={redirectUrl}
          style={{
            minWidth: '100%',
            minHeight: '100vh',
            background: 'transparent',
            border: 'none',
          }}
        />
      )}
    </SyncDocumentAttributes>
  );
};
